import { useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@ownyou/ui-components';
import { useMissions, useUpdateMission } from '../hooks/useMissions';
import { selectTriageMissions } from '../lib/missions/triage';
import { createMissionSpineRunId, recordMissionSpineMarker } from '../lib/mission-spine/instrumentation';

export function Triage() {
  const navigate = useNavigate();
  const { allMissions, isLoading } = useMissions();
  const { acceptMission, dismissMission, snoozeMission } = useUpdateMission();

  const triageMissions = useMemo(() => selectTriageMissions(allMissions), [allMissions]);

  useEffect(() => {
    recordMissionSpineMarker({
      runId: createMissionSpineRunId('slice-02-triage'),
      hop: 'triage_render',
      status: triageMissions.length > 0 ? 'success' : 'skipped',
      reasonCode: triageMissions.length > 0 ? 'triage_cards_available' : 'no_triage_cards',
      counts: {
        rawCount: allMissions.length,
        visibleCount: triageMissions.length,
      },
    }, { log: false });
  }, [allMissions.length, triageMissions.length]);

  const recordAction = (action: 'accept' | 'later' | 'skip') => {
    recordMissionSpineMarker({
      runId: createMissionSpineRunId('slice-02-triage-action'),
      hop: 'triage_action',
      status: 'success',
      reasonCode: action,
      counts: { actionCount: 1 },
    }, { log: false });
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-slate-950 p-4 text-white">
        <p className="text-sm text-slate-400">Loading triage…</p>
      </div>
    );
  }

  return (
    <div data-testid="triage-screen" className="min-h-screen bg-slate-950 text-white">
      <div className="mx-auto max-w-3xl space-y-4 px-4 py-5">
        <div className="flex items-center justify-between gap-3">
          <div>
            <h1 className="text-xl font-semibold text-slate-100">Triage</h1>
            <p data-testid="triage-count" className="mt-1 text-sm text-slate-500">
              {triageMissions.length} needs-review missions
            </p>
          </div>
          <Button variant="secondary" onClick={() => navigate('/')}>Today</Button>
        </div>

        {triageMissions.length === 0 ? (
          <section data-testid="triage-empty" className="rounded-xl border border-slate-700 bg-slate-800 p-4">
            <p className="text-sm text-slate-400">No real low-confidence missions need review.</p>
          </section>
        ) : (
          <div className="space-y-3">
            {triageMissions.map(mission => (
              <article
                key={mission.id}
                data-testid="triage-card"
                data-mission-id={mission.id}
                className="rounded-xl border border-slate-700 bg-slate-800 p-4"
              >
                <h2 className="text-base font-semibold text-slate-100">{mission.title}</h2>
                <p className="mt-1 text-sm text-slate-400">{mission.description ?? mission.reason ?? 'Review this mission before acting.'}</p>
                <div className="mt-3 flex flex-col gap-2 sm:flex-row">
                  <Button
                    data-testid="triage-accept"
                    variant="primary"
                    onClick={() => {
                      recordAction('accept');
                      acceptMission(mission.id).catch(console.warn);
                    }}
                    className="flex-1"
                  >
                    Accept
                  </Button>
                  <Button
                    data-testid="triage-later"
                    variant="secondary"
                    onClick={() => {
                      recordAction('later');
                      snoozeMission(mission.id, 24).catch(console.warn);
                    }}
                    className="flex-1"
                  >
                    Later
                  </Button>
                  <Button
                    data-testid="triage-skip"
                    variant="secondary"
                    onClick={() => {
                      recordAction('skip');
                      dismissMission(mission.id).catch(console.warn);
                    }}
                    className="flex-1"
                  >
                    Skip
                  </Button>
                </div>
              </article>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
